import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

type Counts = Record<string, number>;
interface BackfillResult {
  dryRun: boolean;
  result: { mode: "dry_run"; would_update: Counts } | { mode: "applied"; updated: Counts };
}

export function NfcBackfillPanel() {
  const [loading, setLoading] = useState<"idle" | "scan" | "apply">("idle");
  const [result, setResult] = useState<BackfillResult | null>(null);

  const run = async (confirm: boolean) => {
    setLoading(confirm ? "apply" : "scan");
    try {
      const { data, error } = await supabase.functions.invoke<BackfillResult>(
        "admin-nfc-backfill",
        { body: { confirm } },
      );
      if (error) throw error;
      if (!data) throw new Error("Empty response");
      setResult(data);
      const counts = "would_update" in data.result ? data.result.would_update : data.result.updated;
      const total = Object.values(counts).reduce((a, b) => a + b, 0);
      toast.success(
        confirm ? `Normalized ${total} row${total === 1 ? "" : "s"} to NFC` : `Found ${total} non-NFC row${total === 1 ? "" : "s"}`,
      );
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Backfill failed");
    } finally {
      setLoading("idle");
    }
  };

  const counts: Counts | null = result
    ? "would_update" in result.result
      ? result.result.would_update
      : result.result.updated
    : null;
  const total = counts ? Object.values(counts).reduce((a, b) => a + b, 0) : 0;
  const canApply = !!result && result.dryRun && total > 0;

  return (
    <Card className="shadow-soft">
      <CardHeader>
        <CardTitle>Unicode NFC backfill</CardTitle>
        <CardDescription>
          One-shot normalization of existing rows in <code>excerpts</code> to Unicode NFC.
          New rows are normalized on save (shipped 2026-05-07); this only catches
          pre-existing data. Encrypted columns (excerpt notes) are skipped — they
          normalize on next user edit.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => run(false)} disabled={loading !== "idle"} variant="outline">
            {loading === "scan" ? "Scanning…" : "Scan (dry run)"}
          </Button>
          <Button
            onClick={() => {
              if (!canApply) return;
              if (!confirm(`Normalize ${total} row(s) to NFC? Reversible only via restore.`)) return;
              run(true);
            }}
            disabled={loading !== "idle" || !canApply}
          >
            {loading === "apply" ? "Applying…" : "Apply normalization"}
          </Button>
        </div>

        {counts && (
          <div className="rounded-md border bg-muted/30 p-4 text-sm space-y-2">
            <div className="font-medium">
              {result?.dryRun ? "Would update" : "Updated"}: {total} row{total === 1 ? "" : "s"}
            </div>
            <ul className="space-y-0.5 font-mono text-xs text-muted-foreground">
              {Object.entries(counts).map(([col, n]) => (
                <li key={col} className="flex justify-between gap-4">
                  <span className="truncate">{col}</span>
                  <span>{n}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
